import React, { createContext, FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { slugs } from '@/constants/slugs'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'

interface LimitedDateModalContextType {
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
}

export const LimitedDateModalContext = createContext<LimitedDateModalContextType>({
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
})

interface LimitedDateModalProviderProps {
  children: ReactNode
  theatricalSlug: string
  shouldOpenModal?: boolean
  initialIsOpen?: boolean
}

export const LimitedDateModalProvider: FC<LimitedDateModalProviderProps> = ({
  children,
  theatricalSlug,
  shouldOpenModal = false,
  initialIsOpen = false,
}) => {
  const isSketch = theatricalSlug === slugs.sketch
  const modalStateRef = useRef(initialIsOpen)
  const [isModalOpen, setIsModalOpen] = useState(initialIsOpen)

  useEffect(() => {
    modalStateRef.current = isModalOpen
  }, [isModalOpen])

  const hasSeenPromoRef = useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storageKey = `has-seen-limited-date-promo-${theatricalSlug}`
      hasSeenPromoRef.current = getBooleanFromLocalStorage(storageKey) ?? false
    }
  }, [theatricalSlug])

  useEffect(() => {
    if (shouldOpenModal && isSketch && !modalStateRef.current && !hasSeenPromoRef.current) {
      setIsModalOpen(true)
    }
  }, [shouldOpenModal, isSketch])

  const openModal = useCallback(() => {
    if (isSketch) {
      setIsModalOpen(true)
    }
  }, [isSketch])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    if (typeof window !== 'undefined' && isSketch) {
      writeToLocalStorage(`has-seen-limited-date-promo-${theatricalSlug}`, true)
      hasSeenPromoRef.current = true
    }
  }, [isSketch, theatricalSlug])

  return (
    <LimitedDateModalContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,
      }}
    >
      {children}
    </LimitedDateModalContext.Provider>
  )
}
